import { useEffect } from "react";
import { Add } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SaveButton,
  TextField,
  useAutocomplete,
} from "@pankod/refine-mui";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { IInvoices } from "interfaces";
import { IEnabledModalProps } from "../interfaces";

export const InvoicesModal: React.FC<IEnabledModalProps> = ({
  visible,
  close,
  title,
  data,
  setData,
  canAdd = false,
  setCanAdd,
}) => {
  const {
    saveButtonProps,
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      resource: "null",
    },
  });

  const { autocompleteProps: invoicesAutocompleteProps } =
    useAutocomplete<IInvoices>({
      resource: "invoices",
      onSearch: (searchText) => [
        {
          field: "invoice",
          operator: "contains",
          value: searchText,
        },
      ],
    });

  const cleanInputs = () => {
    setValue("invoice", null);
    setValue("amount", "");
    setValue("qty", "");
    setCanAdd?.(false);
  };

  const onSubmit = (submitData: any, another: boolean) => {
    let newData = {};
    if (data?.id) {
      if (!canAdd) {
        newData = {
          ...submitData,
          id: data?.id,
          action: "update",
        };
      } else {
        newData = {
          ...submitData,
          id: data?.id,
          action: "update",
          delivered_weight:
            parseFloat(getValues("delivered_weight")) +
            parseFloat(data?.delivered_weight),
        };
      }
    } else {
      newData = {
        ...submitData,
        action: "create",
      };
    }
    setData(newData, another);
    cleanInputs();
  };

  useEffect(() => {
    if (data?.id) {
      if (!canAdd) {
        setValue("invoice", data?.invoice);
        setValue("amount", data?.amount);
        setValue("qty", data?.qty);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  let currentInvoice = watch("invoice");

  return (
    <Dialog
      open={visible}
      onClose={() => {
        cleanInputs();
        close();
      }}
      PaperProps={{ sx: { minWidth: 500 } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Controller
            control={control}
            name="invoice"
            rules={{ required: "El campo factura es obligatorio." }}
            defaultValue={null as any}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...invoicesAutocompleteProps}
                  {...field}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  getOptionLabel={(item) => {
                    const label = invoicesAutocompleteProps?.options?.find(
                      (p) => p?.id?.toString() === item?.id?.toString()
                    );

                    if (!label?.invoice) return "";
                    return `${label?.invoice}`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return value === undefined || option.id === value.id;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Factura"
                      margin="normal"
                      variant="outlined"
                      error={!!errors.factura}
                      helperText={errors.factura?.message?.toString()}
                      autoFocus
                      required
                    />
                  )}
                />
              );
            }}
          />
          <TextField
            label="Fecha"
            value={currentInvoice?.date_invoice ?? " "}
            margin="normal"
            variant="outlined"
            disabled
          />
          <TextField
            label="Tipo de moneda"
            value={currentInvoice?.currency ?? " "}
            margin="normal"
            variant="outlined"
            disabled
          />
          <TextField
            {...register("amount", {
              required: "El monto es obligatorio.",
            })}
            onFocus={() => {
              if (getValues("amount") === "0") {
                setValue("amount", "");
              }
            }}
            error={!!errors?.amount}
            helperText={errors.amount?.message?.toString()}
            margin="normal"
            fullWidth
            id="amount"
            label="Monto"
            name="amount"
            type={"number"}
            required
            defaultValue={""}
          />
          <TextField
            {...register("qty", {
              required: "La cantidad de piezas son obligatorias.",
            })}
            onFocus={() => {
              if (getValues("qty") === "0") {
                setValue("qty", "");
              }
            }}
            error={!!errors?.qty}
            helperText={errors.qty?.message?.toString()}
            margin="normal"
            fullWidth
            id="qty"
            label="Cantidad de piezas"
            name="qty"
            type={"number"}
            required
            defaultValue={""}
          />
          {canAdd && (
            <div>
              {data?.delivered_weight} /{" "}
              {+data?.delivered_weight + +watch("delivered_weight")}
            </div>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            cleanInputs();
            close();
          }}
        >
          Cancel
        </Button>
        {!data?.id && (
          <Button
            variant="outlined"
            onClick={(e: React.BaseSyntheticEvent) => {
              e.preventDefault();
              handleSubmit(
                (data) => onSubmit(data, true),
                () => false
              )(e);
            }}
            startIcon={<Add />}
          >
            Agregar otro
          </Button>
        )}
        <SaveButton
          {...saveButtonProps}
          onClick={(e: React.BaseSyntheticEvent) => {
            e.preventDefault();
            handleSubmit(
              (data) => onSubmit(data, false),
              () => false
            )(e);
          }}
        />
      </DialogActions>
    </Dialog>
  );
};
